import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  CLOTH_COLORS,
  EYES,
  EYE_SWATCHES,
  HAIR_COLORS,
  PRESETS,
  SKIN_SWATCHES,
  SLIDERS,
  accessoryKind,
  itemsFor,
  normalizeMh,
  randomizeMh,
} from "../spec.js";
import type { KitItem, MhSpec, SliderGroup, Slot } from "../spec.js";
import type { Focus } from "./CreatorView.js";
import "./creator.css";

// three.js stays out of the settings chunk until the view is shown.
const CreatorView = lazy(() => import("./CreatorView.js"));

const TABS = ["body", "face", "skin", "hair", "beard", "outfit", "accessory"] as const;
type Tab = (typeof TABS)[number];
const STYLES = ["business", "smart", "casual", "sport"] as const;
const HISTORY = 60;

function webgl(): boolean {
  try {
    const c = document.createElement("canvas");
    return Boolean(c.getContext("webgl2") ?? c.getContext("webgl"));
  } catch {
    return false;
  }
}

class Boundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function Swatches({
  label,
  colors,
  value,
  onPick,
  custom,
  testId,
}: {
  label: string;
  colors: readonly string[];
  value: string;
  onPick: (hex: string) => void;
  custom?: string;
  testId?: string;
}) {
  return (
    <div className="mh-field">
      <span className="mh-field-label">{label}</span>
      <div className="mh-swatches" role="radiogroup" aria-label={label} data-testid={testId}>
        {colors.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={value.toLowerCase() === c.toLowerCase()}
            aria-label={c}
            className="mh-swatch"
            style={{ background: c }}
            onClick={() => onPick(c)}
          />
        ))}
        {custom ? (
          <label className="mh-swatch mh-swatch-custom" title={custom}>
            <input
              type="color"
              value={value}
              aria-label={custom}
              onChange={(e) => onPick(e.target.value)}
            />
          </label>
        ) : null}
      </div>
    </div>
  );
}

function Chips({
  label,
  items,
  value,
  onPick,
  none,
  testId,
}: {
  label: string;
  items: readonly { id: string; label: string }[];
  value: string | null;
  onPick: (id: string | null) => void;
  none?: string;
  testId?: string;
}) {
  return (
    <div className="mh-field">
      <span className="mh-field-label">{label}</span>
      <div className="mh-chips" role="group" aria-label={label} data-testid={testId}>
        {none ? (
          <button
            type="button"
            className="mh-chip"
            aria-pressed={value === null}
            onClick={() => onPick(null)}
          >
            {none}
          </button>
        ) : null}
        {items.map((i) => (
          <button
            key={i.id}
            type="button"
            className="mh-chip"
            aria-pressed={value === i.id}
            data-testid={`mh-item-${i.id}`}
            onClick={() => onPick(i.id)}
          >
            {i.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The character creator ("Karakterim"): a live 3D view (orbit, zoom, face or body framing) next to
 * tabs of sliders and choices (body, face, skin, hair, beard, outfit, accessories), with presets,
 * randomise, undo/redo and reset. Controlled: `value` in, `onChange` out; the owner saves.
 */
export function MhCreator({
  value,
  onChange,
  label,
  onSave,
  saveLabel,
  dirty,
  compact = false,
  hookId = "me",
}: {
  value: MhSpec;
  onChange: (next: MhSpec) => void;
  label: string;
  onSave?: () => void;
  saveLabel?: string;
  dirty?: boolean;
  compact?: boolean;
  /** Name of this creator for the test hook `__uzMhCreator(id)`. */
  hookId?: string;
}) {
  const { t } = useTranslation("web");
  const [tab, setTab] = useState<Tab>("body");
  const [focus, setFocus] = useState<Focus>("body");
  const [ready, setReady] = useState(false);
  const past = useRef<MhSpec[]>([]);
  const future = useRef<MhSpec[]>([]);
  const [, bump] = useState(0);
  const has3d = useMemo(() => webgl(), []);
  const spec = useMemo(() => normalizeMh(value), [value]);
  const sex = spec.sex;

  // Sliders push one undo step per drag (the first change after a pause), not one per pixel.
  const lastPush = useRef(0);
  const commit = useCallback(
    (next: MhSpec, coalesce = false) => {
      const now = Date.now();
      if (!coalesce || now - lastPush.current > 600) {
        past.current = [...past.current.slice(-HISTORY + 1), spec];
        future.current = [];
      }
      lastPush.current = coalesce ? now : 0;
      onChange(normalizeMh(next));
      bump((n) => n + 1);
    },
    [spec, onChange],
  );
  const undo = () => {
    const prev = past.current.pop();
    if (!prev) return;
    future.current.push(spec);
    onChange(prev);
    bump((n) => n + 1);
  };
  const redo = () => {
    const next = future.current.pop();
    if (!next) return;
    past.current.push(spec);
    onChange(next);
    bump((n) => n + 1);
  };
  useEffect(() => {
    if (tab === "face" || tab === "hair" || tab === "beard" || tab === "skin") setFocus("face");
    else setFocus("body");
  }, [tab]);

  const setMorph = (id: string, v: number) =>
    commit({ ...spec, morphs: { ...spec.morphs, [id]: v } }, true);
  const itemLabel = (i: KitItem) => t(`mh.items.${i.id}`, { defaultValue: i.id });
  const list = (slot: Slot) => itemsFor(slot, sex).filter((i) => i.sexes.includes(sex));
  const sliders = (group: SliderGroup) => (
    <div className="mh-sliders">
      {SLIDERS.filter((s) => s.group === group).map((s) => {
        const v = spec.morphs[s.id] ?? 0;
        return (
          <label key={s.id} className="mh-slider">
            <span className="mh-slider-head">
              <span>{t(`mh.sliders.${s.id}`, { defaultValue: s.id })}</span>
              <output>{Math.round(v * 100)}</output>
            </span>
            <input
              type="range"
              min={-100}
              max={100}
              step={1}
              value={Math.round(v * 100)}
              data-testid={`mh-slider-${s.id}`}
              onChange={(e) => setMorph(s.id, Number(e.target.value) / 100)}
              onDoubleClick={() => setMorph(s.id, 0)}
            />
          </label>
        );
      })}
    </div>
  );

  const randomize = (what: "body" | "face" | "all") => {
    const seed = (Date.now() ^ Math.floor(Math.random() * 1e9)) >>> 0;
    commit(
      what === "all"
        ? randomizeMh(spec, seed, { looks: true, amount: 0.5 })
        : randomizeMh(spec, seed, { groups: [what], amount: 0.7 }),
    );
  };

  const outfitPanel = (
    <>
      {(["top", "bottom", "shoes"] as const).map((slot) => {
        const items = list(slot);
        const current = slot === "shoes" ? spec.outfit.shoes : spec.outfit[slot];
        return (
          <div key={slot} className="mh-group">
            <h4 className="mh-group-title">{t(`mh.slots.${slot}`)}</h4>
            {STYLES.map((st) => {
              const of = items.filter((i) => i.style === st);
              if (!of.length) return null;
              return (
                <Chips
                  key={st}
                  label={t(`mh.styles.${st}`)}
                  items={of.map((i) => ({ id: i.id, label: itemLabel(i) }))}
                  value={current}
                  onPick={(id) => commit({ ...spec, outfit: { ...spec.outfit, [slot]: id } })}
                  testId={`mh-${slot}-${st}`}
                />
              );
            })}
            <Swatches
              label={t("mh.color")}
              colors={slot === "top" ? CLOTH_COLORS : CLOTH_COLORS.slice(0, 10)}
              value={spec.outfit.colors[slot]}
              custom={t("mh.customColor")}
              onPick={(c) =>
                commit(
                  {
                    ...spec,
                    outfit: { ...spec.outfit, colors: { ...spec.outfit.colors, [slot]: c } },
                  },
                  true,
                )
              }
            />
          </div>
        );
      })}
    </>
  );

  const accessories = itemsFor("accessory");
  const toggleAccessory = (id: string) => {
    const on = spec.accessories.includes(id);
    const rest = spec.accessories.filter((a) => a !== id && accessoryKind(a) !== accessoryKind(id));
    commit({ ...spec, accessories: on ? rest : [...rest, id] });
  };

  const panel: Record<Tab, ReactNode> = {
    body: sliders("body"),
    face: sliders("face"),
    skin: (
      <>
        <Swatches
          label={t("mh.skinTone")}
          colors={SKIN_SWATCHES}
          value={SKIN_SWATCHES[spec.skin] ?? ""}
          onPick={(c) =>
            commit({ ...spec, skin: SKIN_SWATCHES.indexOf(c as (typeof SKIN_SWATCHES)[number]) })
          }
          testId="mh-skin"
        />
        <Swatches
          label={t("mh.eyeColor")}
          colors={EYES.map((e) => EYE_SWATCHES[e] ?? "#777777")}
          value={EYE_SWATCHES[EYES[spec.eyes] ?? ""] ?? ""}
          onPick={(c) =>
            commit({
              ...spec,
              eyes: Math.max(
                0,
                EYES.findIndex((e) => EYE_SWATCHES[e] === c),
              ),
            })
          }
          testId="mh-eyes"
        />
        <Chips
          label={t("mh.brows")}
          items={itemsFor("brows").map((i) => ({ id: i.id, label: itemLabel(i) }))}
          value={spec.brows}
          onPick={(id) => id && commit({ ...spec, brows: id })}
        />
      </>
    ),
    hair: (
      <>
        <Chips
          label={t("mh.hairStyle")}
          items={itemsFor("hair", sex).map((i) => ({ id: i.id, label: itemLabel(i) }))}
          value={spec.hair}
          none={t("mh.noHair")}
          onPick={(id) => commit({ ...spec, hair: id })}
          testId="mh-hair"
        />
        <Swatches
          label={t("mh.hairColor")}
          colors={HAIR_COLORS}
          value={spec.hairColor}
          custom={t("mh.customColor")}
          onPick={(c) => commit({ ...spec, hairColor: c }, true)}
          testId="mh-hair-color"
        />
      </>
    ),
    beard:
      sex === "male" ? (
        <Chips
          label={t("mh.beard")}
          items={itemsFor("beard", sex).map((i) => ({ id: i.id, label: itemLabel(i) }))}
          value={spec.beard}
          none={t("mh.noBeard")}
          onPick={(id) => commit({ ...spec, beard: id })}
          testId="mh-beard"
        />
      ) : (
        <p className="muted small">{t("mh.beardMale")}</p>
      ),
    outfit: outfitPanel,
    accessory: (
      <>
        {(["glasses", "hat"] as const).map((kind) => (
          <div key={kind} className="mh-field">
            <span className="mh-field-label">{t(`mh.acc.${kind}`)}</span>
            <div className="mh-chips">
              {accessories
                .filter((a) => accessoryKind(a.id) === kind)
                .map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="mh-chip"
                    aria-pressed={spec.accessories.includes(a.id)}
                    data-testid={`mh-item-${a.id}`}
                    onClick={() => toggleAccessory(a.id)}
                  >
                    {itemLabel(a)}
                  </button>
                ))}
            </div>
          </div>
        ))}
      </>
    ),
  };

  return (
    <div
      className={`mh-creator${compact ? " is-compact" : ""}`}
      data-testid="mh-creator"
      data-ready={ready ? "1" : "0"}
    >
      <div className="mh-view" aria-label={t("mh.viewLabel", { name: label })} role="img">
        {has3d ? (
          <Boundary fallback={<div className="mh-view-fallback">{t("mh.no3d")}</div>}>
            <Suspense fallback={<div className="mh-view-fallback">{t("mh.loading")}</div>}>
              <CreatorView
                spec={spec}
                focus={focus}
                hookId={hookId}
                onReady={() => setReady(true)}
              />
            </Suspense>
          </Boundary>
        ) : (
          <div className="mh-view-fallback">{t("mh.no3d")}</div>
        )}
        {!ready && has3d ? <div className="mh-view-loading">{t("mh.loading")}</div> : null}
        <div className="mh-view-tools">
          <button
            type="button"
            className="mh-tool"
            aria-pressed={focus === "face"}
            onClick={() => setFocus("face")}
          >
            {t("mh.focus.face")}
          </button>
          <button
            type="button"
            className="mh-tool"
            aria-pressed={focus === "body"}
            onClick={() => setFocus("body")}
          >
            {t("mh.focus.body")}
          </button>
        </div>
      </div>
      <div className="mh-side">
        <div className="mh-top">
          <div className="mh-chips" role="radiogroup" aria-label={t("mh.sex")}>
            {(["male", "female"] as const).map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={sex === s}
                className="mh-chip"
                data-testid={`mh-sex-${s}`}
                onClick={() => sex !== s && commit(PRESETS[s][0]!.spec)}
              >
                {t(`mh.sexes.${s}`)}
              </button>
            ))}
          </div>
          <div className="mh-actions">
            <button
              type="button"
              className="mh-tool"
              onClick={undo}
              disabled={!past.current.length}
              data-testid="mh-undo"
            >
              {t("mh.undo")}
            </button>
            <button
              type="button"
              className="mh-tool"
              onClick={redo}
              disabled={!future.current.length}
            >
              {t("mh.redo")}
            </button>
            <button
              type="button"
              className="mh-tool"
              onClick={() => randomize("all")}
              data-testid="mh-random"
            >
              {t("mh.randomAll")}
            </button>
          </div>
        </div>
        <div className="mh-field">
          <span className="mh-field-label">{t("mh.presets")}</span>
          <div className="mh-chips mh-presets" data-testid="mh-presets">
            {PRESETS[sex].map((p) => (
              <button
                key={p.id}
                type="button"
                className="mh-chip"
                data-testid={`mh-preset-${p.id}`}
                onClick={() => commit(p.spec)}
              >
                {t(`mh.preset.${p.id}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="mh-tabs" role="tablist" aria-label={label}>
          {TABS.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              className="mh-tab"
              data-testid={`mh-tab-${k}`}
              onClick={() => setTab(k)}
            >
              {t(`mh.tabs.${k}`)}
            </button>
          ))}
        </div>
        <div className="mh-panel" role="tabpanel">
          {tab === "body" || tab === "face" ? (
            <div className="mh-panel-tools">
              <button type="button" className="mh-tool" onClick={() => randomize(tab)}>
                {t("mh.randomize")}
              </button>
              <button
                type="button"
                className="mh-tool"
                onClick={() =>
                  commit({
                    ...spec,
                    morphs: Object.fromEntries(
                      Object.entries(spec.morphs).filter(
                        ([k]) => SLIDERS.find((s) => s.id === k)?.group !== tab,
                      ),
                    ),
                  })
                }
              >
                {t("mh.reset")}
              </button>
            </div>
          ) : null}
          {panel[tab]}
        </div>
        {onSave ? (
          <div className="mh-save">
            <button
              type="button"
              className="btn btn-primary"
              disabled={dirty === false}
              onClick={onSave}
              data-testid="mh-save"
            >
              {saveLabel ?? t("mh.save")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
